import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Input,
  CheckBox,
  Button,
  MessageStrip,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function DocumentsConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const documentSettings = systemSettings?.DOCUMENT_SEQUENCE;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('DocumentsConfig - Current Values:', { formData, documentSettings });
  }, [formData, documentSettings]);

  if (!formData || !documentSettings) {
    return <div>Error: Faltan datos necesarios para la configuración de documentos</div>;
  }

  const handleSequenceChange = (documentId, field, value) => {
    setFormData({
      ...formData,
      sequences: {
        ...formData.sequences,
        [documentId]: {
          ...formData.sequences?.[documentId],
          [field]: value,
        },
      },
    });
  };

  const getDocumentById = (id) => documentSettings.documents.find(doc => doc.id === id);

  const prepareDataForSave = () => ({
    ...documentSettings,
    documents: documentSettings.documents.map(doc => ({
      ...doc,
      sequence: formData.sequences?.[doc.id] || {
        currentSequence: '1',
        from: '1',
        to: '999999',
        autoAssigned: false,
      },
    })),
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () =>
    documentSettings.documents.some(doc => {
      const current = formData.sequences?.[doc.id];
      const dflt = {
        currentSequence: '1',
        from: '1',
        to: '999999',
        autoAssigned: documentSettings.sequenceConfig.autoAssigned.default,
      };
      return JSON.stringify(current) !== JSON.stringify(dflt);
    });

  const renderSequenceConfig = (documentId) => {
    const document = getDocumentById(documentId);
    if (!document) return null;
    const seq = formData.sequences?.[documentId] || {};

    return (
      <div key={documentId} style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{document.name}</Title>
        </div>

        <div style={CONFIG_STYLES.formGrid3}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Secuencia Actual')}</Label>
            <Input
              type="Number"
              value={seq.currentSequence || '1'}
              style={{ width: '100%' }}
              onChange={(e) => handleSequenceChange(documentId, 'currentSequence', e.target.value)}
              min="1"
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Desde')}</Label>
            <Input
              type="Number"
              value={seq.from || '1'}
              style={{ width: '100%' }}
              onChange={(e) => handleSequenceChange(documentId, 'from', e.target.value)}
              min="1"
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Hasta')}</Label>
            <Input
              type="Number"
              value={seq.to || '999999'}
              style={{ width: '100%' }}
              onChange={(e) => handleSequenceChange(documentId, 'to', e.target.value)}
              min="1"
            />
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <CheckBox
            text={autoT('Auto asignado por sistema')}
            checked={seq.autoAssigned || false}
            onChange={(e) => handleSequenceChange(documentId, 'autoAssigned', e.target.checked)}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración de Secuencias')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Configure las secuencias de numeración para cada tipo de documento')}
          </Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {documentSettings.documents.map(doc => renderSequenceConfig(doc.id))}

      {/* Info */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="information" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Información')}</Title>
        </div>
        <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
          <li><Text>{autoT('La secuencia actual debe estar dentro del rango especificado (Desde - Hasta)')}</Text></li>
          <li><Text>{autoT('Al llegar al número máximo, la secuencia volverá al número inicial')}</Text></li>
          <li><Text>{autoT('La opción "Auto asignado" permite que el sistema gestione automáticamente la numeración')}</Text></li>
        </ul>
      </div>
    </div>
  );
}

export default DocumentsConfig;
